/**
 * Phase 7.2 — Modal "Detalle del movimiento".
 *
 * Three sections:
 *   1. Raw data (timestamp / sku / qty / folio / source / raw_type)
 *   2. Current classification + single-doc reclassify
 *   3. Read-only audit history (reclassification_history array)
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { History, Loader2, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import inventoryReview from "../../api/inventoryReview";
import { formatApiError } from "../../api/client";

function Row({ label, value, mono = false, testId }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-border/40 last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={`col-span-2 text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MovementDetailModal({
  open,
  onOpenChange,
  movementId,
  canonicalTypes,
  onReclassified,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [newType, setNewType] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch detail when opened
  useEffect(() => {
    if (!open || !movementId) {
      setDoc(null);
      setNewType("");
      setReason("");
      return;
    }
    setLoading(true);
    inventoryReview
      .getMovement(movementId)
      .then(({ data }) => {
        setDoc(data);
        setNewType(data.movement_canonical_type || "");
      })
      .catch((err) => {
        toast.error(formatApiError(err));
      })
      .finally(() => setLoading(false));
  }, [open, movementId]);

  const currentType = doc?.movement_canonical_type;
  const dirty = newType && newType !== currentType;

  const save = async () => {
    if (!dirty || !movementId) return;
    setSaving(true);
    try {
      await inventoryReview.reclassify({
        movement_ids: [movementId],
        new_canonical_type: newType,
        reason,
      });
      toast.success(
        t("inventory.review.reclassifySingleOk", {
          defaultValue: "Movimiento reclasificado",
        }),
      );
      if (onReclassified) onReclassified({ updated: 1 });
      // Reload local doc so the history section refreshes.
      const { data } = await inventoryReview.getMovement(movementId);
      setDoc(data);
      setNewType(data.movement_canonical_type || "");
      setReason("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const history = doc?.reclassification_history || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="movement-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="movement-detail-title">
            {doc?.sku
              ? `SKU ${doc.sku}`
              : t("inventory.review.detailTitle", {
                  defaultValue: "Detalle del movimiento",
                })}
          </DialogTitle>
          <DialogDescription>
            {t("inventory.review.detailSubtitle", {
              defaultValue:
                "Inspeccionar datos crudos, cambiar la clasificación o revisar el historial.",
            })}
          </DialogDescription>
        </DialogHeader>

        {loading || !doc ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-3/5" />
            <Skeleton className="h-5 w-2/5" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-5 w-3/5" />
          </div>
        ) : (
          <ScrollArea className="max-h-[55vh] pr-2">
            {/* Section 1 — raw data */}
            <section className="space-y-0.5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                {t("inventory.review.detailRaw", {
                  defaultValue: "Datos crudos",
                })}
              </h4>
              <Row label="Timestamp" value={fmtDate(doc.timestamp)} mono />
              <Row label="SKU" value={doc.sku} mono testId="detail-sku" />
              <Row label="Qty" value={doc.qty} mono />
              <Row label="Δ Stock actual" value={doc.stock_delta} mono />
              <Row label="Folio" value={doc.folio} mono />
              <Row label="Source" value={doc.source_report} />
              <Row label="Raw type" value={doc.movement_raw_type} mono />
              <Row label="Empleado" value={doc.employee} />
              {doc.bin_location ? (
                <Row label="Bin" value={doc.bin_location} mono />
              ) : null}
            </section>

            {/* Section 2 — current classification + change */}
            <section className="space-y-2 mt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("inventory.review.detailClassification", {
                  defaultValue: "Clasificación",
                })}
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t("inventory.review.detailCurrent", {
                    defaultValue: "Actual:",
                  })}
                </span>
                <Badge
                  variant="outline"
                  className="font-mono"
                  data-testid="detail-current-type"
                >
                  {currentType || "—"}
                </Badge>
                {doc.needs_user_review ? (
                  <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30">
                    needs review
                  </Badge>
                ) : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-2 mt-1">
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger data-testid="detail-new-type">
                    <SelectValue
                      placeholder={t("inventory.review.selectType", {
                        defaultValue: "Reclasificar como...",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {canonicalTypes.map((ct) => (
                      <SelectItem
                        key={ct.name}
                        value={ct.name}
                        data-testid={`detail-type-opt-${ct.name}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{ct.name}</span>
                          <span className="text-xs text-muted-foreground">
                            · {ct.display_name}
                          </span>
                          {ct.is_system ? (
                            <Badge
                              variant="outline"
                              className="ml-1 text-[10px] py-0 px-1"
                            >
                              sys
                            </Badge>
                          ) : null}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="detail-save-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1.5" />
                  )}
                  {t("common.save", { defaultValue: "Guardar" })}
                </Button>
              </div>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 200))}
                maxLength={200}
                rows={2}
                placeholder={t("inventory.review.reasonPlaceholder", {
                  defaultValue: "Motivo (opcional, máx 200 chars)",
                })}
                disabled={!dirty || saving}
                data-testid="detail-reason"
                className="resize-none text-sm"
              />
            </section>

            {/* Section 3 — history */}
            <section className="space-y-2 mt-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" />
                {t("inventory.review.detailHistory", {
                  defaultValue: "Historial de reclasificaciones",
                })}
                <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1">
                  {history.length}
                </Badge>
              </h4>
              {history.length === 0 ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="detail-history-empty"
                >
                  {t("inventory.review.detailHistoryEmpty", {
                    defaultValue: "Sin cambios previos.",
                  })}
                </p>
              ) : (
                <ul
                  className="space-y-1.5"
                  data-testid="detail-history-list"
                >
                  {history.map((h, idx) => (
                    <li
                      key={idx}
                      className="rounded-md border border-border/60 p-2 text-xs"
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-mono text-muted-foreground">
                          {h.from_canonical_type || "—"}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono font-medium">
                          {h.to_canonical_type}
                        </span>
                      </div>
                      <div className="text-muted-foreground">
                        {fmtDate(h.changed_at)} ·{" "}
                        {h.changed_by_name || h.changed_by || "—"}
                      </div>
                      {h.reason ? (
                        <div className="mt-0.5 italic text-muted-foreground/80">
                          "{h.reason}"
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="detail-close-btn"
          >
            {t("common.close", { defaultValue: "Cerrar" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
